import { useQuery } from "@apollo/client";
import { CheckCircle, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { AppointmentDetailFragment, Customer } from "@/gql/graphql";
import { CUSTOMERS_QUERY } from "@/graphql/customers";
import { useSalonStore } from "@/lib/store/salon";
import { useReassignCustomer } from "../hooks/use-reassign-customer";

interface ReassignCustomerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointment: AppointmentDetailFragment;
}

export function ReassignCustomerDialog({
  open,
  onOpenChange,
  appointment,
}: ReassignCustomerDialogProps) {
  const { currentSalon } = useSalonStore();
  const { reassignCustomer, isReassigning } = useReassignCustomer();
  const { t: tForms } = useTranslation("forms");
  const { t: tErrors } = useTranslation("errors");
  const { t: tAppointments } = useTranslation("appointments");

  // Switch customer fields
  const [customerInput, setCustomerInput] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Customer search query
  const { data: customersData, loading: customersLoading } = useQuery(CUSTOMERS_QUERY, {
    variables: {
      salonId: currentSalon?.id || "",
      filters: {
        search: customerInput.trim() || undefined,
        isActive: true,
      },
    },
    skip: !currentSalon?.id || !customerInput.trim(),
  });

  // Initialize form when dialog opens
  useEffect(() => {
    if (open) {
      setCustomerInput("");
      setCustomerEmail("");
      setCustomerPhone("");
      setSelectedCustomer(null);
      setShowSuggestions(false);
    }
  }, [open]);

  // Handle customer selection from suggestions
  const handleCustomerSelect = (selectedCust: Customer) => {
    setSelectedCustomer(selectedCust);
    setCustomerInput(`${selectedCust.firstName} ${selectedCust.lastName}`);
    setCustomerEmail(selectedCust.email || "");
    setCustomerPhone(selectedCust.primaryPhone?.phone || "");
    setShowSuggestions(false);
  };

  // Handle customer input change
  const handleCustomerInputChange = (input: string) => {
    setCustomerInput(input);
    setSelectedCustomer(null);
    setCustomerEmail("");
    setCustomerPhone("");
    setShowSuggestions(true);
  };

  const handleSave = async () => {
    if (!currentSalon) return;

    // Validate customer selection
    if (!customerInput.trim()) {
      toast.error(tErrors("forms.requiredField"));
      return;
    }

    const success = await reassignCustomer({
      salonId: currentSalon.id,
      appointmentId: appointment.id,
      selectedCustomer,
      customerInput,
      customerEmail,
      customerPhone,
    });

    if (success) {
      onOpenChange(false);
    }
  };

  const isFormValid = () => {
    // Customer field must have value (either selected or entered)
    return customerInput.trim().length > 0;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{tForms("titles.reassignCustomer")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="relative space-y-3">
            <Label htmlFor="customerSearch">{tForms("labels.customerName")}</Label>
            <Input
              ref={inputRef}
              id="customerSearch"
              placeholder={tForms("placeholders.searchOrCreateCustomer")}
              value={customerInput}
              onChange={(e) => handleCustomerInputChange(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            />

            {/* Customer Suggestions Dropdown */}
            {showSuggestions && customerInput.trim() && (
              <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-48 overflow-y-auto">
                {customersLoading ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="ml-2 text-sm">
                      {tAppointments("booking.customerStep.messages.searching")}
                    </span>
                  </div>
                ) : customersData?.customers?.customers?.length ? (
                  customersData.customers.customers.map((cust) => (
                    <button
                      key={cust.id}
                      type="button"
                      className="w-full text-left p-3 hover:bg-accent cursor-pointer border-b last:border-b-0"
                      onClick={() => handleCustomerSelect(cust as Customer)}
                    >
                      <div className="font-medium">
                        {cust.firstName} {cust.lastName}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {cust.email && cust.primaryPhone?.phone
                          ? `${cust.email} • ${cust.primaryPhone.phone}`
                          : cust.email ||
                            cust.primaryPhone?.phone ||
                            tAppointments("booking.customerStep.messages.noContactInfo")}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="p-3 text-sm text-muted-foreground text-center">
                    {tAppointments("booking.customerStep.messages.noCustomersFound")}
                  </div>
                )}
              </div>
            )}

            {/* Status indicator */}
            {customerInput.trim() && (
              <div className="text-sm mt-3">
                {selectedCustomer ? (
                  <div className="text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 p-2 rounded flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      {tAppointments("booking.customerStep.messages.selectedCustomer", {
                        firstName: selectedCustomer.firstName,
                        lastName: selectedCustomer.lastName,
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-foreground bg-muted p-2 rounded flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      {tAppointments("booking.customerStep.messages.willCreateNew", {
                        name: customerInput,
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-5">
              <div className="space-y-3">
                <Label htmlFor="customerEmail">{tForms("labels.email")}</Label>
                <Input
                  id="customerEmail"
                  type="email"
                  placeholder={tForms("placeholders.email")}
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  disabled={!!selectedCustomer}
                  readOnly={!!selectedCustomer}
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="customerPhone">{tForms("labels.phone")}</Label>
                <Input
                  id="customerPhone"
                  type="tel"
                  placeholder={tForms("placeholders.phone")}
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  disabled={!!selectedCustomer}
                  readOnly={!!selectedCustomer}
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-3 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isReassigning}>
            {tForms("actions.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={!isFormValid() || isReassigning}>
            {isReassigning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {tForms("actions.reassignAppointment")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
